import {
  AttachmentCategory,
  AttachmentType,
  type InlineAttachmentCreateDto,
  type TechnologySelectDto,
} from '@@types/api.types';
import { type BufferedAttachment, innovatorUploadBufferedAttachments } from '@queries/attachments.query';

import { Modal } from '@components/core/modal.component.tsx';
import { InformationStep } from '@components/pages/technology/components/technology-technology-information-step.component.tsx';
import { TechnologyResearchStep } from '@components/pages/technology/components/technology-technology-research-step.component';
import { SuccessStep } from '@components/pages/technology/components/technology-technology-success-step.component.tsx';
import { UsesAndDomainStep } from '@components/pages/technology/components/technology-technology-uses-and-domain-step.component.tsx';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component.tsx';
import { ModalSteps, type StepConfig, useSteps } from '@components/shared/modal-steps.component.tsx';
import { useAppForm } from '@components/ui/form/form';
import { useApi } from '@hooks/api.hook';
import { errorToast } from '@hooks/toast.hook';
import { useCreateTechnology, useDeleteTechnology, useUpdateTechnology } from '@queries/technology.query.ts';
import { routes } from '@routes';
import {
  type TechnologyFormData,
  informationStepSchema,
  researchStepSchema,
  technologySchema,
  typeStepSchema,
  usesAndDomainStepSchema,
} from '@schemas/technology.schema';
import { useStore } from '@tanstack/react-form';
import { getSmallestItemByKey } from '@utils/array.util';
import { createFormInvalidHandler } from '@utils/form.util';
import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { technologyFormOptions } from './technology-form-options';
import { TypeStep } from './technology-technology-type-step.component';

type TechnologyModalProps = {
  initialStep?: Steps;
  isEdit: boolean;
  isOpen: boolean;
  onClose: () => void;
  technology?: TechnologySelectDto;
  width?: string;
};

export enum Steps {
  TYPE = 0,
  INFORMATION = 1,
  USES_AND_DOMAIN = 2,
  RESEARCH = 3,
}

const stepData: StepConfig[] = [
  {
    label: 'Type',
    step: Steps.TYPE,
    guidance: 'Which type best describes your technology?',
  },
  {
    label: 'Information',
    step: Steps.INFORMATION,
    guidance: 'Tell us more about your technology.',
  },
  {
    label: 'Uses and Domain',
    step: Steps.USES_AND_DOMAIN,
    guidance: 'Within which primary use and domain is your technology intended to operate?',
  },
  {
    label: 'Research',
    step: Steps.RESEARCH,
  },
];

const stepSchemaMap = {
  [Steps.TYPE]: typeStepSchema,
  [Steps.INFORMATION]: informationStepSchema,
  [Steps.USES_AND_DOMAIN]: usesAndDomainStepSchema,
  [Steps.RESEARCH]: researchStepSchema,
};

function mapBufferedToInlineDto(buffered: BufferedAttachment): InlineAttachmentCreateDto {
  const { category, contentType, description, featured, size, title, type, url } = buffered;
  return { category, contentType, description, featured, size, title, type, url };
}

function upsertBufferedAttachment(params: {
  attachment: BufferedAttachment;
  attachments: BufferedAttachment[];
}): BufferedAttachment[] {
  const { attachment, attachments } = params;
  const hasExistingAttachment = attachments.some(
    (existingAttachment) => existingAttachment.clientId === attachment.clientId,
  );
  const nextAttachments = hasExistingAttachment
    ? attachments.map((existingAttachment) => {
        return existingAttachment.clientId === attachment.clientId ? attachment : existingAttachment;
      })
    : [...attachments, attachment];

  if (attachment.category !== AttachmentCategory.TECHNOLOGY_IMAGE || !attachment.featured) {
    return nextAttachments;
  }

  return nextAttachments.map((existingAttachment) => {
    if (
      existingAttachment.category !== AttachmentCategory.TECHNOLOGY_IMAGE ||
      existingAttachment.clientId === attachment.clientId
    ) {
      return existingAttachment;
    }

    return {
      ...existingAttachment,
      featured: false,
    };
  });
}

function buildFormDefaultValues(params: {
  isEdit: boolean;
  technology?: TechnologySelectDto;
}): TechnologyFormData {
  const { isEdit, technology } = params;

  if (isEdit && technology) {
    const primaryUse = technology.uses.length > 0 ? getSmallestItemByKey(technology.uses, 'position') : undefined;
    const primaryDomain =
      primaryUse?.domains && primaryUse.domains.length > 0
        ? getSmallestItemByKey(primaryUse.domains, 'position')
        : undefined;

    return {
      categoryId: technology.category.id,
      name: technology.name,
      description: technology.description || '',
      type: technology.type,
      subcategoryId: technology.subcategoryId,
      primaryUseId: primaryUse?.useId || '',
      primaryDomainId: primaryDomain?.id || '',
      primarySubdomainId: primaryDomain?.subdomainIds?.[0] || '',
      primaryUseDescription: primaryUse?.description || '',
      timeline: technology.timeline
        ? {
            description: technology.timeline.description,
            startDate: technology.timeline.startDate,
            validated: technology.timeline.validated,
          }
        : undefined,
      metadata: technology.metadata ?? { researchKeywords: [] },
    };
  }

  return technologyFormOptions.defaultValues;
}

export const TechnologyTechnologyModal: FC<TechnologyModalProps> = (props) => {
  const { initialStep = Steps.TYPE, isEdit, isOpen, onClose, technology, width = '640px' } = props;

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<BufferedAttachment[]>([]);

  const { apiPost } = useApi();

  const {
    mutateAsync: createTechnology,
    data: createTechnologyResponse,
    isPending: isCreatePending,
    isSuccess: createSuccess,
  } = useCreateTechnology();
  const { mutateAsync: updateTechnology, isPending: isUpdatePending } = useUpdateTechnology();
  const { mutateAsync: deleteTechnology, isPending: isDeletePending } = useDeleteTechnology();
  const navigate = useNavigate();

  const form = useAppForm({
    ...technologyFormOptions,
    defaultValues: buildFormDefaultValues({ isEdit, technology }),
    validators: {
      onSubmit: ({ formApi }) => {
        const currentStepSchema = stepSchemaMap[step];
        const errors = formApi.parseValuesWithSchema(currentStepSchema);
        if (errors) return errors;
      },
    },
    canSubmitWhenInvalid: true,
    onSubmitInvalid: createFormInvalidHandler(),

    onSubmit: async ({ value }) => {
      if (step < Steps.RESEARCH && !isEdit) {
        updateStepState(step);
        setStep(step + 1);
        return;
      }

      const validationResult = technologySchema.safeParse(value);
      if (!validationResult.success) {
        errorToast({ description: 'Please fill in all required fields correctly.' });
        return;
      }

      if (isEdit) {
        if (!technology) return;
        await updateTechnology({ id: technology.id, ...validationResult.data });
        onCloseOverride();
      } else {
        const inlineAttachments = pendingAttachments.map(mapBufferedToInlineDto);
        const fileAttachments = pendingAttachments.filter((a) => a.type === AttachmentType.FILE && a.file);

        const result = await createTechnology({
          ...validationResult.data,
          attachments: inlineAttachments.length > 0 ? inlineAttachments : undefined,
        });

        if (result.pendingUploads && result.pendingUploads.length > 0) {
          await innovatorUploadBufferedAttachments({
            apiPost,
            fileAttachments,
            onError: ({ message }) => {
              errorToast({ description: message });
            },
            pendingUploads: result.pendingUploads,
            technologyId: result.technology.id,
          });
        }

        onCloseOverride();
        setIsSuccessModalOpen(true);
      }
    },
  });

  const {
    step,
    setStep,
    stepsInfo,
    updateStepState,
    handleStepClick: handleStepClickBase,
    resetSteps,
  } = useSteps(stepData, initialStep, isEdit);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const eventTarget = e.target;
    const isWithinCommandOrPopover =
      eventTarget instanceof HTMLElement ? eventTarget.closest('[role="combobox"]') : null;

    if (e.key === 'Enter' && !(e.target instanceof HTMLTextAreaElement || isWithinCommandOrPopover)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  const resetForm = () => {
    form.reset();
    resetSteps();
    setPendingAttachments([]);
  };

  const onCloseOverride = () => {
    resetForm();
    onClose();
  };

  const handleBufferSave = (data: BufferedAttachment) => {
    setPendingAttachments((previousAttachments) => {
      return upsertBufferedAttachment({
        attachment: data,
        attachments: previousAttachments,
      });
    });
  };

  const handleBufferEdit = (_clientId: string, data: BufferedAttachment) => {
    setPendingAttachments((previousAttachments) => {
      return upsertBufferedAttachment({
        attachment: data,
        attachments: previousAttachments,
      });
    });
  };

  const handleBufferRemove = (clientId: string) => {
    setPendingAttachments((prev) => prev.filter((a) => a.clientId !== clientId));
  };

  const formErrors = useStore(form.store, (state) => state.errors);

  const handleBack = () => {
    setStep((prevStep) => prevStep - 1);
  };

  const handleStepClick = (targetStep: Steps) => {
    if (formErrors.length > 0) {
      errorToast({ description: 'Please fill in all required fields correctly.' });
      return;
    }

    handleStepClickBase(targetStep);
  };

  const handleDelete = async () => {
    if (!isEdit || !technology) return;

    await deleteTechnology(
      { id: technology.id },
      {
        onSuccess: () => {
          navigate(routes.technologies.path);
        },
      },
    );
  };

  const currentStepData = stepData.find((data) => data.step === step);
  const stepContentByStep = {
    [Steps.TYPE]: <TypeStep form={form} />,
    [Steps.INFORMATION]: <InformationStep form={form} />,
    [Steps.USES_AND_DOMAIN]: <UsesAndDomainStep form={form} />,
    [Steps.RESEARCH]: (
      <TechnologyResearchStep
        bufferedAttachments={pendingAttachments}
        form={form}
        isEdit={isEdit}
        onBufferEdit={handleBufferEdit}
        onBufferRemove={handleBufferRemove}
        onBufferSave={handleBufferSave}
        targetId={technology?.id}
      />
    ),
  } satisfies Record<Steps, ReactNode>;

  if (isSuccessModalOpen && createSuccess) {
    const primaryUse = createTechnologyResponse?.technology.uses[0];
    const primaryDomain = primaryUse?.domains[0];
    const usesPayload = [{ useId: primaryUse?.useId, domainId: primaryDomain?.id }];

    return (
      <SuccessStep
        onClose={() => {
          setIsSuccessModalOpen(false);
          onCloseOverride();
        }}
        technologyId={createTechnologyResponse?.technology.id || ''}
        uses={usesPayload}
      />
    );
  }

  return (
    <Modal
      footer={{
        rightButton: {
          disabled: isDeletePending,
          loading: isCreatePending || isUpdatePending,
          onClick: form.handleSubmit,
          children: isEdit ? 'Update' : step === Steps.RESEARCH ? 'Add Technology' : 'Continue',
        },
        leftButton: isEdit
          ? {
              disabled: isUpdatePending,
              loading: isDeletePending,
              onClick: () => setIsDeleteModalOpen(true),
              children: 'Delete',
              variant: 'destructive',
            }
          : step !== Steps.TYPE
            ? {
                onClick: handleBack,
                children: 'Back',
                variant: 'secondary',
              }
            : undefined,
      }}
      header={{ title: `${isEdit ? 'Update' : 'Add'} Technology` }}
      isOpen={isOpen}
      onClose={onCloseOverride}
      width={width}
    >
      {isEdit && (
        <ConfirmDeleteModal
          isOpen={isDeleteModalOpen}
          itemName={technology?.name || 'technology'}
          onConfirm={handleDelete}
          setIsOpen={setIsDeleteModalOpen}
        />
      )}

      <div className="flex flex-col gap-4 py-4">
        <ModalSteps
          stepsInfo={stepsInfo}
          onStepClick={handleStepClick}
        />

        <div className="flex flex-col p-4 gap-4">
          {currentStepData?.guidance && (
            <div className="text-md font-normal text-[#1F1C1AB2] whitespace-pre-wrap">{currentStepData?.guidance}</div>
          )}

          <div className="h-full w-full">{stepContentByStep[step]}</div>
        </div>
      </div>
    </Modal>
  );
};
